import { defineComponent as _defineComponent } from 'vue'
import { Message, MessageBox } from 'element-ui';
import { ref, onMounted, onUnmounted, Ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import RoomHeader from './components/RoomHeader/index.vue';
import RoomContent from './components/RoomContent/index.vue';
import RoomSetting from './components/RoomSetting/index.vue';
import RoomVote from './components/RoomVote/index.vue';
import { useBasicStore } from '@/stores/basic';
import { useRoomStore } from '@/stores/room';
import { useChatStore } from '@/stores/chat';

import TUIRoomCore, {
  ETUIRoomEvents, ETUIRoomRole, ETUISpeechMode, TRTCStatistics, ETUIRoomMuteType,RoomInfo
} from './tui-room-core';
import { debounce, throttle } from './utils/utils';
import logger from './tui-room-core/common/logger/logs';
import TUIRoomAegis from './utils/aegis';
import { MESSAGE_DURATION } from '../constants/message';
import { LIVE_STAGE } from "@/constants/room";
import {LAYOUT} from "@/constants/render";
import StringUtils from "@/putils/StringUtils";
import {ROOM_LIVE_STAGE, USER_ROLE_MAJOR} from "@/constants/localKeys";
import StorageUtils from "@/putils/StorageUtils";

interface RoomParam {
	isOpenCamera: boolean,
	isOpenMicrophone: boolean,
	defaultCameraId: string,
	defaultMicrophoneId: string,
	defaultSpeakerId: string,
}

interface RoomInitData {
  sdkAppId: number,
  userId: string,
  imUserId: string,
  userSig: string,
  userName: string,
  userAvatar: string,
  shareUserId?: string,
  userRole?: string,
  shareUserSig?: string,
  roomInfos:RoomInfo,
  votemsgContent: string,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  emits: [
  'on-log-out', 'on-create-room', 'on-enter-room',
  'on-exit-room', 'on-destroy-room', 'on-kick-off',
  'create-room', 'enter-room'
],
  setup(__props, { expose, emit }) {

expose({
  init,
  createRoom,
  enterRoom,
});



const logPrefix = '[Room]';

const basicStore = useBasicStore();
const roomStore = useRoomStore();
const chatStore = useChatStore();

const { sdkAppId, userId,roomId } = storeToRefs(basicStore);

// 处理页面鼠标悬浮显示工具栏逻辑
const roomContentRef = ref<InstanceType<typeof RoomContent>>();
const showRoomTool: Ref<boolean> = ref(true);
const roomRef: Ref<Node | undefined> = ref();
function handleHideRoomTool() {
  showRoomTool.value = false;
}

const handleHideRoomToolDebounce = debounce(handleHideRoomTool, 5000);
const handleHideRoomToolThrottle = throttle(handleHideRoomToolDebounce, 1000);

onMounted(() => {
  roomRef.value?.addEventListener('mouseenter', () => {
    showRoomTool.value = true;
    handleHideRoomToolDebounce();
  });
  roomRef.value?.addEventListener('click', () => {
    showRoomTool.value = true;
    handleHideRoomToolDebounce();
  }, false);
  roomRef.value?.addEventListener('mousemove', () => {
    showRoomTool.value = true;
    handleHideRoomToolThrottle();
  });
  roomRef.value?.addEventListener('mouseleave', () => {
    showRoomTool.value = false;
  });
});

onUnmounted(() => {
  roomRef.value?.removeEventListener('mouseenter', () => {
    showRoomTool.value = true;
    handleHideRoomToolDebounce();
  });
  roomRef.value?.removeEventListener('click', () => {
    showRoomTool.value = true;
    handleHideRoomToolDebounce();
  }, false);
  roomRef.value?.removeEventListener('mousemove', () => {
    showRoomTool.value = true;
    handleHideRoomToolDebounce();
  });
  roomRef.value?.removeEventListener('mouseleave', () => {
    showRoomTool.value = false;
  });
});

async function init(option: RoomInitData) {
  basicStore.setBasicInfo(option);
  roomStore.setLocalUser(option);

  const { sdkAppId, userId, userSig, userName, userAvatar, userRole } = option;

  if (!StringUtils.isObjEqual(userRole,USER_ROLE_MAJOR)) {
    basicStore.setLayout(LAYOUT.TOP_SIDE_LIST);
    emit('create-room', "ApplySpeech");
  }

  await TUIRoomCore.login(sdkAppId, userId, userRole, userSig);


  let roomLiveStage = StorageUtils.getSessionItem(ROOM_LIVE_STAGE,{userId:userId})
  if (roomLiveStage != LIVE_STAGE.ONGOING){
    basicStore.setLiveStageRoom(LIVE_STAGE.NOT_STARTED)
  }else{
    basicStore.setLiveStageRoom(roomLiveStage)
  }

  TUIRoomCore.updateMyProfile({
    nick: userName,
    avatar: userAvatar || '',
  });
}

async function createRoom(
  roomId: number,
  roomMode: ETUISpeechMode = ETUISpeechMode.FREE_SPEECH,
  roomParam?: RoomParam,
) {
  basicStore.setRoomId(roomId);
  basicStore.setRole(ETUIRoomRole.MASTER);
  logger.debug(`${logPrefix}createRoom:`, roomId, roomMode, roomParam);
  let checkRoom = await TUIRoomCore.checkRoomExistence(roomId);
  if (!checkRoom){
    await TUIRoomCore.createRoom(roomId, roomMode);
  }else{
    await TUIRoomCore.enterRoom(roomId);
  }
  const roomInfo = await TUIRoomCore.getRoomInfo();
  console.log("----------- 获取到的房间信息 -- createRoom --roomInfo -------------------");
  console.log(roomParam)
  basicStore.setRoomInfo(roomInfo);
  // setRoomParam 必须在 setRoomInfo 之后，因为 roomInfo 中有是否开启全员禁麦禁画的信息
  roomStore.setRoomParam(roomParam);
  basicStore.setSharingStatus(false);
  let roomLiveStage = StorageUtils.getSessionItem(ROOM_LIVE_STAGE,{userId:userId.value})
  if (roomLiveStage != LIVE_STAGE.ONGOING){
    setLiveStageRoomIndex();
  }else{
    basicStore.setLiveStageRoom(roomLiveStage)
  }
  emit('on-create-room', {code: 0, message: 'create room success'});
  TUIRoomAegis.reportEvent({ name: 'createRoom', ext1: 'createRoom-success' });
}

//todo 设置播放状态
async function setLiveStageRoomIndex(){
  setTimeout(() => {
    basicStore.setLiveStageRoom(LIVE_STAGE.NOT_STARTED)
  }, 2000);
}

async function enterRoom(roomId: number, roomParam?: RoomParam) {
  basicStore.setRoomId(roomId);
  logger.debug(`${logPrefix}enterRoom:`, roomId, roomParam);
  await TUIRoomCore.enterRoom(roomId);
  const roomInfo = await TUIRoomCore.getRoomInfo();
  basicStore.setRoomInfo(roomInfo);
  // setRoomParam 必须在 setRoomInfo 之后，因为 roomInfo 中有是否开启全员禁麦禁画的信息
  roomStore.setRoomParam(roomParam);
  emit('on-enter-room', {
    code: 0,
    message: 'enter room success',
  });
  let roomLiveStage = StorageUtils.getSessionItem(ROOM_LIVE_STAGE,{userId:userId.value})
  if (roomLiveStage != LIVE_STAGE.ONGOING){
    setLiveStageRoomIndex();
  }else{
    basicStore.setLiveStageRoom(roomLiveStage)
  }
  TUIRoomAegis.reportEvent({ name: 'enterRoom', ext1: 'enterRoom-success' });
}

const onUserVoiceVolume = (eventInfo: []) => {
  roomStore.setAudioVolume(eventInfo);
};

const onNetworkQuality = (localQuality: Record<string, any>) => {
  basicStore.setLocalQuality(localQuality.quality);
};

const onStatistics = (statistics: TRTCStatistics) => {
  basicStore.setStatistics(statistics);
};

const onReceivedChatMessage = (message: any) => {
  chatStore.updateMessageList(message);
};

function resetStore() {
  basicStore.reset();
  chatStore.reset();
  roomStore.reset();
}

const logOut = () => {
  resetStore();
  emit('on-log-out');
};

const onDestroyRoom = (info: { code: number; message: string, data:any}) => {
  resetStore();
  emit('on-destroy-room', info);
};

const onExitRoom = (info: { code: number; message: string, data:any }) => {
  resetStore();
  emit('on-exit-room', info);
};

const onMicrophoneMuted = (data: {mute: boolean, muteType: ETUIRoomMuteType}) => {
  if (data.muteType === ETUIRoomMuteType.MasterMuteAll) {
    const tipMessage = data.mute ? '主持人已开启全体静音' : '主持人已解除全体静音';
    Message({
      type: 'warning',
      message: tipMessage,
      duration: MESSAGE_DURATION.LONG,
    });
    basicStore.setIsMuteAllAudio(data.mute);
    // 如果主持人解除全员禁言，不主动调起用户麦克风
    if (data.mute) {
      roomStore.setIsLocalAudioMuted(true);
      TUIRoomCore.muteLocalMicrophone(true);
    }
    basicStore.setCanControlSelfAudio(!data.mute);
  }

  if (data.muteType === ETUIRoomMuteType.MasterMuteCurrentUser) {
    const tipMessage = data.mute ? '主持人已关闭您的麦克风' : '主持人已允许您开启麦克风';
    Message({
      type: 'warning',
      message: tipMessage,
      duration: MESSAGE_DURATION.LONG,
    });
    if (data.mute) {
      roomStore.setIsLocalAudioMuted(true);
      TUIRoomCore.muteLocalMicrophone(true);
      // 主持人开启全员禁言时，单独打开再关闭单人的麦克风，此时对应用户的麦克风状态为无法操作
      basicStore.setCanControlSelfAudio(!basicStore.isMuteAllAudio);
    } else {
      basicStore.setCanControlSelfAudio(true);
    }
  }
};

const onCameraMuted = (data: {mute: boolean; muteType: ETUIRoomMuteType}) => {
  if (data.muteType === ETUIRoomMuteType.MasterMuteAll) {
    basicStore.setIsMuteAllVideo(data.mute);
    const tipMessage = data.mute ? '主持人已开启全体禁画' : '主持人已解除全体禁画';
    Message({
      type: 'warning',
      message: tipMessage,
      duration: MESSAGE_DURATION.LONG,
    });
    // 如果主持人解除全员禁画，不主动调起用户摄像头
    if (data.mute) {
      roomStore.setIsLocalVideoMuted(true);
      TUIRoomCore.stopCameraPreview();
    }
    basicStore.setCanControlSelfVideo(!data.mute);
  }

  if (data.muteType === ETUIRoomMuteType.MasterMuteCurrentUser) {
    const tipMessage = data.mute ? '主持人已关闭您的摄像头' : '主持人已允许你开启摄像头';
    Message({
      type: 'warning',
      message: tipMessage,
      duration: MESSAGE_DURATION.NORMAL,
    });
    if (data.mute) {
      roomStore.setIsLocalVideoMuted(true);
      TUIRoomCore.stopCameraPreview();
      // 主持人开启全员禁画时，单独打开再关闭单人的摄像头，此时对应用户的摄像头状态为无法操作
      basicStore.setCanControlSelfVideo(!basicStore.isMuteAllVideo);
    } else {
      basicStore.setCanControlSelfVideo(true);
    }
  }
};

const onUserChatRoomMuted = (data: {mute: boolean; muteType: ETUIRoomMuteType}) => {
  const tipMessage = data.mute ? '您被主持人禁止文字聊天' : '您被主持人允许文字聊天';
  Message({
    type: 'warning',
    message: tipMessage,
    duration: MESSAGE_DURATION.NORMAL,
  });
  if (data.muteType === ETUIRoomMuteType.MasterMuteCurrentUser) {
    chatStore.setIsMuteChatByMater(data.mute);
  }
};

const onUserKickOff = async () => {
  try {
    const response = await TUIRoomCore.exitRoom();
    await TUIRoomCore.logout();
    logger.log(`${logPrefix}leaveRoom:`, response);
    resetStore();
    MessageBox.alert('被主持人请出房间', '通知', {
      confirmButtonText: '确认',
      callback: async () => {
        emit('on-kick-off', { code: 0, data: {roomId:roomId.value,userId:userId.value} });
      },
    });
  } catch (error) {
    logger.error(`${logPrefix}onUserKickOff error:`, error);
  }
};

function handlePageLeave() {
  if (!basicStore.isMaster) {
    TUIRoomCore.exitRoom();
  }
}

// 页面刷新或者管理
function beforeunloadFn() {
  handlePageLeave();
}

const onRoonVote = async (data: any) => {
    console.log("收到投票通知消息------------------------------------------");
    let vote=JSON.parse(data.text)
    console.log(vote.msgContent);
    basicStore.setVotemsgContent(vote.msgContent);
}

onMounted(async () => {
  TUIRoomCore.enableAudioVolumeEvaluation(100);
  TUIRoomCore.on(ETUIRoomEvents.onUserVoiceVolume, onUserVoiceVolume);
  TUIRoomCore.on(ETUIRoomEvents.onNetworkQuality, onNetworkQuality);
  TUIRoomCore.on(ETUIRoomEvents.onStatistics, onStatistics);
  TUIRoomCore.on(ETUIRoomEvents.onReceiveChatMessage, onReceivedChatMessage);
  TUIRoomCore.on(ETUIRoomEvents.onMicrophoneMuted, onMicrophoneMuted);
  TUIRoomCore.on(ETUIRoomEvents.onCameraMuted, onCameraMuted);
  TUIRoomCore.on(ETUIRoomEvents.onUserChatRoomMuted, onUserChatRoomMuted);
  TUIRoomCore.on(ETUIRoomEvents.onUserKickOff, onUserKickOff);
  TUIRoomCore.on(ETUIRoomEvents.onRoonVote, onRoonVote);
  window.addEventListener('beforeunload', beforeunloadFn);
});

onUnmounted(() => {
  handlePageLeave();
  TUIRoomCore.off(ETUIRoomEvents.onUserVoiceVolume, onUserVoiceVolume);
  TUIRoomCore.off(ETUIRoomEvents.onNetworkQuality, onNetworkQuality);
  TUIRoomCore.off(ETUIRoomEvents.onStatistics, onStatistics);
  TUIRoomCore.off(ETUIRoomEvents.onReceiveChatMessage, onReceivedChatMessage);
  TUIRoomCore.off(ETUIRoomEvents.onMicrophoneMuted, onMicrophoneMuted);
  TUIRoomCore.off(ETUIRoomEvents.onCameraMuted, onCameraMuted);
  TUIRoomCore.off(ETUIRoomEvents.onUserChatRoomMuted, onUserChatRoomMuted);
  TUIRoomCore.off(ETUIRoomEvents.onUserKickOff, onUserKickOff);
  TUIRoomCore.off(ETUIRoomEvents.onRoonVote, onRoonVote);
  window.removeEventListener('beforeunload', beforeunloadFn);
});

watch(sdkAppId, (val) => {
  if (val) {
    TUIRoomAegis.setSdkAppId(val);
    TUIRoomAegis.reportEvent({
      name: 'loaded',
      ext1: 'loaded-success',
    });
  }
});

//开始混流


return { __sfc: true,emit, logPrefix, basicStore, roomStore, chatStore, sdkAppId, userId, roomId, roomContentRef, showRoomTool, roomRef, handleHideRoomTool, handleHideRoomToolDebounce, handleHideRoomToolThrottle, init, createRoom, setLiveStageRoomIndex, enterRoom, onUserVoiceVolume, onNetworkQuality, onStatistics, onReceivedChatMessage, resetStore, logOut, onDestroyRoom, onExitRoom, onMicrophoneMuted, onCameraMuted, onUserChatRoomMuted, onUserKickOff, handlePageLeave, beforeunloadFn, onRoonVote, RoomHeader, RoomContent, RoomSetting, RoomVote }
}

})